import { useState } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { loginSchema, type LoginInput } from '@shared/validation/auth-schemas';
import { useAuth } from '../../hooks/useAuth';
import { useAuthStore, isDevMode, type DevRole } from '../../stores/auth-store';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { api } from '../../lib/api-client';
import type { User } from '../../stores/auth-store';
import logoFull from '../../assets/logo-octopaint.jpeg';

function mapAuthError(error: unknown): string {
  if (!(error instanceof Error)) return 'Error al iniciar sesión. Intente nuevamente.';
  const msg = error.message;
  if (msg.includes('NotAuthorizedException') || msg.includes('Incorrect username or password')) {
    return 'Correo o contraseña incorrectos';
  }
  if (msg.includes('UserNotFoundException')) {
    return 'No existe una cuenta con este correo';
  }
  if (msg.includes('FORCE_NEW_PASSWORD')) {
    return 'Debe cambiar su contraseña temporal. Contacte al administrador.';
  }
  if (msg.includes('TooManyRequestsException') || msg.includes('LimitExceededException')) {
    return 'Su cuenta ha sido bloqueada temporalmente. Intente en 15 minutos.';
  }
  if (msg.includes('UserNotConfirmedException') || msg.includes('Cuenta no confirmada')) {
    return 'Cuenta no confirmada. Revise su email.';
  }
  if (msg.includes('NetworkError') || msg.includes('Network')) {
    return 'Error de conexión. Verifique su internet.';
  }
  return 'Error al iniciar sesión. Intente nuevamente.';
}

export function LoginPage() {
  const navigate = useNavigate();
  const { login, isLoggingIn } = useAuth();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [serverError, setServerError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
  });

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const onSubmit = async (data: LoginInput) => {
    setServerError('');
    try {
      await login(data);
      navigate('/', { replace: true });
    } catch (err) {
      setServerError(mapAuthError(err));
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="blob-blue" />
      <div className="blob-orange" />
      <div className="blob-dark" />

      <Card className="relative z-10 w-full max-w-md fade-up" padding="lg">
        <div className="text-center mb-8">
          <img src={logoFull} alt="Octopaint" className="mx-auto h-16 object-contain" />
          <p className="mt-2 text-sm text-gray-500">Plataforma de Gestión de Obra</p>
        </div>

        {serverError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {serverError}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input
            label="Correo electrónico"
            type="email"
            placeholder="usuario@octopaint.com"
            error={errors.email?.message}
            disabled={isLoggingIn}
            {...register('email')}
          />

          <Input
            label="Contraseña"
            type="password"
            placeholder="••••••••"
            error={errors.password?.message}
            disabled={isLoggingIn}
            {...register('password')}
          />

          <Button
            type="submit"
            fullWidth
            isLoading={isLoggingIn}
            loadingText="Verificando..."
          >
            Ingresar
          </Button>
        </form>

        <div className="mt-4 text-right">
          <Link
            to="/recuperar-password"
            className="text-sm text-brand-blue hover:underline"
          >
            ¿Olvidaste tu contraseña?
          </Link>
        </div>

        {/* Dev login — only when VITE_AUTH_MODE=dev */}
        {isDevMode && (
          <DevLoginPanel onLogin={() => navigate('/', { replace: true })} />
        )}
      </Card>
    </div>
  );
}

// ── Dev Login Panel (VITE_AUTH_MODE=dev) ────────────────────────────

const DEV_ROLES: Array<{ role: DevRole; label: string }> = [
  { role: 'gerente', label: 'Gerente' },
  { role: 'operaciones', label: 'Operaciones' },
  { role: 'administrativo', label: 'Administrativo' },
  { role: 'residente', label: 'Residente' },
  { role: 'compras', label: 'Compras' },
];

function DevLoginPanel({ onLogin }: { onLogin: () => void }) {
  const { setUser, setDevRole, setDevLoggedIn } = useAuthStore();
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState('');

  const handleDevLogin = async (role: DevRole) => {
    setLoading(role);
    setError('');
    try {
      // Set role first so the interceptor uses the right X-Dev-User-Id
      setDevRole(role);
      // Call real backend to get user data
      const response = await api.get<{ data: User }>('/me');
      setUser(response.data.data);
      setDevLoggedIn(true);
      onLogin();
    } catch {
      setError(`Error al conectar con el backend. Asegúrese de que el dev server está corriendo en el puerto 4000.`);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="mt-6 border-t border-gray-200 pt-4">
      <p className="text-xs text-gray-400 text-center mb-3">Dev Login (AUTH_MODE=dev)</p>
      {error && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        {DEV_ROLES.map((dev) => (
          <button
            key={dev.role}
            onClick={() => handleDevLogin(dev.role)}
            disabled={loading !== null}
            className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-[#3c98ff] hover:text-white hover:border-[#3c98ff] transition-colors disabled:opacity-50"
          >
            {loading === dev.role ? 'Conectando...' : dev.label}
            <span className="block text-[10px] opacity-70">{dev.role}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
